/*
 * Sportsact: sticky "Læg i kurv" på mobil (sections/custom-sticky-atc.liquid)
 *
 * - Viser baren, når Impacts hovedknap ikke er synlig (eller først efter scroll forbi den), og skjuler den ved footeren.
 * - Variantvalget styrer Impacts egen variant-picker, så pris, lager, billeder og URL opdateres som normalt.
 * - Lytter på Impacts "variant:change"-event og kopierer pris/knaptilstand fra produktsiden.
 * Ingen eksterne biblioteker.
 */
class CustomStickyAtc extends HTMLElement {
  #observer;
  #abortController;
  #mainVisible = true;
  #mainPassed = false;
  #footerVisible = false;

  connectedCallback() {
    this.form = document.getElementById(this.getAttribute('form'))
      || document.querySelector(`form[id^="product-form-${this.getAttribute('product-id')}-"]`);

    if (!this.form) {
      return;
    }

    // Obs: form.id peger på formularens <input name="id">, derfor getAttribute('id')
    this.formId = this.form.getAttribute('id');

    // Hvis formularen blev fundet via fallback, skal knappen pege på den rigtige formular
    this.querySelectorAll('[form]').forEach((element) => element.setAttribute('form', this.formId));

    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    this.#observer = new IntersectionObserver(this.#onIntersection.bind(this));
    this.#observeTargets();

    this.form.addEventListener('variant:change', this.#onVariantChange.bind(this), { signal });
    this.querySelector('.custom-sticky-atc__select')?.addEventListener('change', this.#onSelectChange.bind(this), { signal });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#abortController?.abort();
  }

  // Formularen observeres (ikke selve knappen), fordi Impact udskifter <buy-buttons> ved variantskift
  #observeTargets() {
    this.#observer.disconnect();
    this.mainButton = this.form;
    this.#observer.observe(this.mainButton);

    const footer = document.querySelector('.footer');
    if (footer) {
      this.#observer.observe(footer);
    }
  }

  #onIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.target === this.mainButton) {
        this.#mainVisible = entry.isIntersecting;
        this.#mainPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      } else {
        this.#footerVisible = entry.isIntersecting;
      }
    });

    const wantedByMode = this.getAttribute('show-mode') === 'after_scroll' ? this.#mainPassed : !this.#mainVisible;
    this.classList.toggle('is-visible', wantedByMode && !this.#footerVisible);
  }

  // Sætter de tilsvarende valg i Impacts variant-picker og lader Impact klare resten
  #onSelectChange(event) {
    const option = event.target.selectedOptions[0];
    const optionIds = (option?.dataset.optionIds || '').split(',').filter(Boolean);
    const formId = this.formId;
    const changedInputs = [];

    optionIds.forEach((valueId, index) => {
      const input = document.querySelector(`input[data-option-position="${index + 1}"][form="${formId}"][value="${CSS.escape(valueId)}"]`);

      if (input && !input.checked) {
        changedInputs.push(input);
      }
    });

    if (changedInputs.length === 0) {
      return;
    }

    changedInputs.forEach((input) => (input.checked = true));
    changedInputs.at(-1).dispatchEvent(new Event('change', { bubbles: true }));
  }

  #onVariantChange(event) {
    const variant = event.detail.variant;
    const select = this.querySelector('.custom-sticky-atc__select');

    if (variant && select) {
      select.value = String(variant.id);
    }

    const mainPrice = document.querySelector(`.shopify-section--main-product [data-block-type="price"] price-list`);
    const stickyPrice = this.querySelector('.custom-sticky-atc__price');

    if (mainPrice && stickyPrice) {
      const clone = mainPrice.cloneNode(true);
      clone.classList.remove('price-list--lg');
      stickyPrice.replaceChildren(clone);
    }

    const image = this.querySelector('.custom-sticky-atc__image');
    const mediaSrc = variant?.featured_media?.preview_image?.src;

    if (image && mediaSrc) {
      const url = new URL(mediaSrc, window.location.origin);
      url.searchParams.set('width', '120');
      image.removeAttribute('srcset');
      image.src = url.toString();
    }

    const mainButton = this.form.querySelector('buy-buttons button[type="submit"]');
    const stickyButton = this.querySelector('.custom-sticky-atc__button');

    if (mainButton && stickyButton) {
      stickyButton.disabled = mainButton.disabled;
      // Knaptekst uden beløbet (det sætter assets/custom-atc-price.js selv)
      const label = mainButton.cloneNode(true);
      label.querySelectorAll('.custom-atc-price, .button__loader').forEach((node) => node.remove());
      stickyButton.querySelector('.custom-sticky-atc__button-label').textContent = label.textContent.trim();
    }
  }
}

if (!window.customElements.get('custom-sticky-atc')) {
  window.customElements.define('custom-sticky-atc', CustomStickyAtc);
}
